'use es6';

import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import Url from 'urlinator/Url';
import { cancelMeeting as cancelMeetingAction, fetchEventDetails as fetchEventDetailsAction } from '../../redux/actions/BookActions';
import { track } from 'MeetingsLib/tracking/UsageTracker';
import { getMeetingsCancelStatus, getMeetingsCancelError } from '../../selectors/RequestStatusSelectors';
import { getBookInfo as getBookInfoSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import ProfileHeader from '../header/ProfileHeader';
import { STARTED, SUCCEEDED, FAILED } from 'MeetingsBase/constants/RequestStatusTypes';
import { LINK_TYPES, MEETING_TYPES } from 'MeetingsBase/constants/enums';
import UIAlert from 'UIComponents/alert/UIAlert';
import { getMeetingSourceFromLocation, getDisplayAvatarOption } from 'MeetingsLib/utils/utils';
import { EXCHANGE_CANCELLATION, MEETING_TEMPORARILY_UNAVAILABLE } from 'MeetingsLib/constants/errors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CancelConfirmContainer = styled.div.withConfig({
  displayName: "CancelContainer__CancelConfirmContainer",
  componentId: "sc-1k6io1l-0"
})(["max-width:600px;margin:0 auto;padding-top:48px;"]);
function SuccessState({
  profile,
  avatar
}) {
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(ProfileHeader, {
      profile: profile,
      cancelling: true,
      booked: false,
      avatar: avatar
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "cancel.success.description"
    })]
  });
}
export default function CancelContainer({
  profile,
  displayInfo
}) {
  const dispatch = useDispatch();
  useEffect(() => {
    track('meetingEventInteraction', {
      action: 'viewed cancel',
      source: getMeetingSourceFromLocation()
    });
  }, []);
  const cancelRequestStatus = useSelector(state => getMeetingsCancelStatus(state));
  const cancelRequestError = useSelector(state => getMeetingsCancelError(state));
  const isCancellingExchangeError = cancelRequestError === EXCHANGE_CANCELLATION;
  const isCancellingDuringMigrationError = cancelRequestError === MEETING_TEMPORARILY_UNAVAILABLE;
  const slug = useSelector(state => state.userSlug);
  const meetingChangeId = useMemo(() => {
    const url = new Url(window.location);
    return url.paramValue('meetingChangeId') || url.paramValue('cancelId');
  }, []);
  useEffect(() => {
    if (isCancellingExchangeError) {
      Raven.captureMessage('Attempted to cancel Exchange meeting', {
        extra: {
          slug,
          meetingChangeId
        },
        level: 'error'
      });
    } else if (isCancellingDuringMigrationError) {
      Raven.captureMessage('Attempted to cancel meeting during active migration', {
        extra: {
          slug,
          meetingChangeId
        },
        level: 'error'
      });
    }
  }, [isCancellingExchangeError, isCancellingDuringMigrationError, meetingChangeId, slug]);
  const eventDetails = useSelector(state => state.eventDetails);
  useEffect(() => {
    dispatch(fetchEventDetailsAction(slug, meetingChangeId));
  }, [slug, meetingChangeId, dispatch]);
  const cancelMeeting = useCallback(() => {
    dispatch(cancelMeetingAction(slug, meetingChangeId));
  }, [dispatch, slug, meetingChangeId]);
  const linkType = useSelector(state => getBookInfoSelector(state).linkType);
  const linkIsPersonal = linkType === LINK_TYPES.get(MEETING_TYPES.PERSONAL);
  const {
    publicDisplayAvatarOption,
    avatar,
    companyAvatar
  } = displayInfo;
  const avatarUrl = getDisplayAvatarOption(publicDisplayAvatarOption, avatar, companyAvatar, linkIsPersonal);
  const cancelRequestFailedMessageKey = isCancellingDuringMigrationError ? 'cancel.failure.alertOrEmail' : 'cancel.failure.alert';
  const cancelForm = /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(ProfileHeader, {
      profile: profile,
      booked: false,
      cancelling: true,
      avatar: avatarUrl
    }), eventDetails && /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "cancel.confirm.description",
        options: {
          eventName: eventDetails.eventName,
          meetingDateTime: /*#__PURE__*/_jsx(FormattedDateTime, {
            value: eventDetails.startTime,
            format: "LLLL"
          })
        }
      })
    }), /*#__PURE__*/_jsx(UILoadingButton, {
      use: "primary",
      loading: cancelRequestStatus === STARTED,
      failed: cancelRequestStatus === FAILED,
      onClick: cancelMeeting,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "cancel.confirm.cancel"
      })
    }), cancelRequestStatus === FAILED && /*#__PURE__*/_jsx(UIAlert, {
      use: "inline",
      className: "m-top-4",
      type: "danger",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: cancelRequestFailedMessageKey
      })
    })]
  });
  return /*#__PURE__*/_jsx(CancelConfirmContainer, {
    children: /*#__PURE__*/_jsx(UICardWrapper, {
      className: "width-100 p-all-0 height-100",
      children: /*#__PURE__*/_jsx(UICardSection, {
        className: "p-all-0 height-100",
        children: /*#__PURE__*/_jsx("div", {
          className: "m-x-4 p-y-11 p-x-3 text-center",
          children: cancelRequestStatus === SUCCEEDED ? /*#__PURE__*/_jsx(SuccessState, {
            profile: profile,
            eventDetails: eventDetails,
            avatar: avatarUrl
          }) : cancelForm
        })
      })
    })
  });
}